/**
 * 日次【集計】(⑥指示: 日次／週次／月次を切替)。現行シート「日次【集計】」の再現。
 *  親=アポインター ／ 子=架電会社(期間内の同じ会社を合算)。行をクリックすると子行を開閉(既定は閉じる。⑦指示)
 *  列の並びは現行シートに合わせる(⑪指示): RANK／アポインター／出勤形態／報酬／架電会社／架電内容／取得数 → 時間別 → 通話・取得効率 → BB稼働 → 売上・報酬・原価率
 *  先頭2列は横スクロールしても固定。列グループの境に薄い縦線。説明の吹き出しは見出しだけ(表の中には出さない)
 *  条件付き色は colors.ts(現行シートの条件付き書式)
 *  最下部に現行シートと同じ「報酬別の稼働」(時給／単価ごとの 稼働人数・稼働時間・取得件数)
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import HelpTip, { type Help } from '@core/components/HelpTip';
import { IconChevronRight } from '@core/components/icons';
import MultiSelect, { matchesFilter } from '@core/components/MultiSelect';
import { SearchBox, SortTh, matchKeyword, sortRows, useSort } from '@core/components/tableTools';
import CsvUploadModal from '@core/import/CsvUploadModal';
import KintaiModal, { fmtKintaiCheck } from './KintaiModal';
import { formatYen } from '@core/lib/money';
import { BUSINESS_LABELS, HOUR_BANDS, type BusinessType } from '@core/lib/types';
import { apphOf, derived, fmtDuration, fmtNum, fmtPct, groupByAppointer, rankOf, styleOf, sumAgg, type Aggregate, type AppointerGroup, type Rank, type StaffMetrics } from './calc';
import { HELP, apphClass, costRateClass, hitRateClass, hourlyClass, rankClass, talkPerApptClass, workPerApptClass } from './colors';
import PeriodToolbar from './PeriodToolbar';
import { usePeriodData } from './usePeriod';

type SortKey =
  | 'rank'
  | 'name'
  | 'style'
  | 'wage'
  | 'company'
  | 'business'
  | 'appointments'
  | 'calls'
  | 'hit_rate'
  | 'talk_per_appt'
  | 'work_per_appt'
  | 'apph'
  | 'reception'
  | 'talk'
  | 'after'
  | 'work'
  | 'hours'
  | 'calls_per_hour'
  | 'unit'
  | 'revenue'
  | 'pay'
  | 'cost_rate';

/** 子行: 期間内の同じ架電会社を合算した1行 */
type CompanyChild = {
  company_id: string | null;
  style: string;
  business: BusinessType;
  is_manager: boolean;
  wage: number | null;
  unit_price: number | null;
  days: number;
  agg: Aggregate;
  /** 合算前の行(日次なら1行 = BB ID × 会社 × 日) */
  rows: StaffMetrics[];
};

function childrenByCompany(g: AppointerGroup): CompanyChild[] {
  const map = new Map<string, StaffMetrics[]>();
  for (const r of g.children) map.set(r.company_id ?? '', [...(map.get(r.company_id ?? '') ?? []), r]);
  return [...map.values()].map((list) => {
    const first = list[0]!;
    const uniq = <T,>(pick: (r: StaffMetrics) => T) => Array.from(new Set(list.map(pick)));
    const wages = uniq((r) => r.wage);
    const units = uniq((r) => r.unit_price);
    return {
      company_id: first.company_id,
      style: uniq(styleOf).join('／'),
      business: (list.every((r) => r.business === 'fuyohin') ? 'fuyohin' : 'kyutoki') as BusinessType,
      is_manager: first.is_manager,
      wage: wages.length === 1 ? wages[0]! : null,
      unit_price: units.length === 1 ? units[0]! : null,
      days: new Set(list.map((r) => r.date)).size,
      agg: sumAgg(list),
      rows: list,
    };
  });
}

/** 出勤形態の並び順(⑧指示): 管理者 → 品川 → タイミー → 在宅時給 → 在宅歩合。同じ形態では給湯器 → 不用品 */
const STYLE_ORDER: Record<string, number> = { '管理/時給': 0, '出勤/時給': 1, 'タイミー/時給': 2, '在宅/時給': 3, '在宅/歩合': 4 };
function styleRank(style: string, business: BusinessType): number {
  const first = style.split('／')[0] ?? style;
  return (STYLE_ORDER[first] ?? 9) + (business === 'fuyohin' ? 0.5 : 0);
}

/** RANKの並べ替え値: S>A>B+>B->C の順、同じRANK内は見込/h(不用品は10倍で判定した値) */
function rankScore(rank: Rank, agg: Aggregate, business: BusinessType): number {
  const order: Record<Rank, number> = { S: 5, A: 4, 'B+': 3, 'B-': 2, C: 1, '—': 0 };
  const apph = apphOf(agg) ?? 0;
  return order[rank] * 1000 + (business === 'fuyohin' ? apph * 10 : apph);
}

function aggValue(agg: Aggregate, key: SortKey): number | null {
  const d = derived(agg);
  switch (key) {
    case 'appointments':
      return agg.appointments;
    case 'calls':
      return agg.calls;
    case 'hit_rate':
      return d.hit_rate;
    case 'talk_per_appt':
      return d.talk_per_appt;
    case 'work_per_appt':
      return d.work_per_appt;
    case 'apph':
      return d.apph;
    case 'reception':
      return agg.reception_sec;
    case 'talk':
      return agg.talk_sec;
    case 'after':
      return agg.after_sec;
    case 'work':
      return d.work_sec;
    case 'hours':
      return agg.hours;
    case 'calls_per_hour':
      return d.calls_per_hour;
    case 'revenue':
      return agg.revenue;
    case 'pay':
      return agg.pay;
    case 'cost_rate':
      return d.cost_rate;
    default:
      return null;
  }
}

/**
 * 最下部の区分別合計(現行シート「日次【集計】」の 品川／ホワイト／在宅／合計 の行)。
 *  品川 = 品川・タイミー・管理者(給湯器) ／ ホワイト = 不用品 ／ 在宅 = 在宅(時給・歩合)
 *  合計 = 品川＋在宅(給湯器。RANK もこの値)。ホワイトを含めた全体は別行に出す
 */
type SubtotalKey = 'shinagawa' | 'white' | 'zaitaku';
const SUBTOTALS: { key: SubtotalKey; label: string }[] = [
  { key: 'shinagawa', label: '出勤' },
  { key: 'white', label: 'ホワイト' },
  { key: 'zaitaku', label: '在宅' },
];
function subtotalOf(r: StaffMetrics): SubtotalKey {
  if (r.business === 'fuyohin') return 'white';
  return r.work_style === 'zaitaku' ? 'zaitaku' : 'shinagawa';
}

export default function StaffSummaryPage() {
  const period = usePeriodData();
  const { rows, targets, companyMap, companies, summaries, loading, range, mode, reload } = period;
  const { isAdmin } = useAuth();
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const [styleF, setStyleF] = useState<Set<string>>(new Set());
  const [bizF, setBizF] = useState<Set<BusinessType>>(new Set());
  const [companyF, setCompanyF] = useState<Set<string>>(new Set());
  const [keyword, setKeyword] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  /** 出勤の勤怠の確認モーダル(日次・OPレポート取込済みの日だけ) */
  const [kintaiOpen, setKintaiOpen] = useState(false);
  // 既定の並びは出勤形態(管理者→品川→在宅時給→在宅歩合)。同じ形態の中は見込/h の高い順(groupByAppointer の順)
  const { sort, toggle: toggleSort } = useSort<SortKey>({ key: 'style', dir: 'asc' });

  const companyName = (id: string | null) => (id ? (companyMap.get(id)?.name ?? id) : '割振なし');

  const styleOptions = useMemo(() => Array.from(new Set(rows.map(styleOf))).sort().map((s) => ({ key: s, label: s })), [rows]);
  const companyOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.company_id ?? '')))
        .map((id) => ({ id, name: companyName(id || null) }))
        .sort((a, b) => a.name.localeCompare(b.name, 'ja')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, companyMap],
  );

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) => matchesFilter(styleF, styleOf(r)) && matchesFilter(bizF, r.business) && matchesFilter(companyF, r.company_id ?? '') && matchKeyword(keyword, r.name, r.op_code, r.bb_id),
      ),
    [rows, styleF, bizF, companyF, keyword],
  );

  const groupWage = (g: AppointerGroup) => (g.is_manager ? null : Array.from(new Set(g.children.map((r) => r.wage))).length === 1 ? g.children[0]!.wage : null);
  const groupUnit = (g: AppointerGroup) => (Array.from(new Set(g.children.map((r) => r.unit_price))).length === 1 ? g.children[0]!.unit_price : null);

  const groups = useMemo(() => {
    const base = groupByAppointer(filteredRows).map((g) => ({ g, children: childrenByCompany(g) }));
    const sorted = sortRows(base, sort, ({ g, children }, key) => {
      if (key === 'name') return g.name;
      if (key === 'style') return styleRank(g.style, g.business);
      if (key === 'wage') return groupWage(g);
      if (key === 'unit') return groupUnit(g);
      if (key === 'business') return g.businesses;
      if (key === 'company') return children.length === 1 ? companyName(children[0]!.company_id) : `${children.length}社`;
      if (key === 'rank') return rankScore(g.rank, g.agg, g.business);
      return aggValue(g.agg, key);
    });
    return sorted.map(({ g, children }) => ({
      g,
      children: sortRows(children, sort ?? { key: 'appointments', dir: 'desc' }, (c, key) => {
        if (key === 'name') return g.name;
        if (key === 'style') return styleRank(c.style, c.business);
        if (key === 'wage') return c.wage;
        if (key === 'unit') return c.unit_price;
        if (key === 'business') return BUSINESS_LABELS[c.business];
        if (key === 'company') return companyName(c.company_id);
        if (key === 'rank') return rankScore(rankOf(apphOf(c.agg), c.business), c.agg, c.business);
        return aggValue(c.agg, key);
      }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, sort, companyMap]);

  const total = useMemo(() => sumAgg(filteredRows), [filteredRows]);
  const subtotals = useMemo(() => {
    const by = new Map<SubtotalKey, StaffMetrics[]>();
    for (const r of filteredRows) by.set(subtotalOf(r), [...(by.get(subtotalOf(r)) ?? []), r]);
    const kyutoki = filteredRows.filter((r) => r.business !== 'fuyohin');
    return {
      rows: SUBTOTALS.map((s) => ({ ...s, agg: sumAgg(by.get(s.key) ?? []) })),
      kyutoki: sumAgg(kyutoki),
      hasWhite: (by.get('white') ?? []).length > 0,
    };
  }, [filteredRows]);
  const callRate = targets?.call_rate_per_min ?? 2.88;

  const toggleOpen = (id: string) =>
    setOpened((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const BASIC = 7;
  const colCount = BASIC + HOUR_BANDS.length + 5 + 8 + 4;

  /** 出勤(品川・タイミー)で OPレポートの稼働がある行(勤怠の確認の対象)。日次だけ */
  const isShukkin = (r: StaffMetrics) => (r.work_style === 'shinagawa' || r.work_style === 'timee') && r.shift_hours !== null;
  const shukkinRows = useMemo(() => (mode === 'day' ? rows.filter(isShukkin) : []), [rows, mode]);
  /** 親(アポインター)行・子(会社)行が日次の出勤1行だけを表すとき、その行(実稼働の吹き出しと「手」の印に使う) */
  const shukkinOf = (children: CompanyChild[]): StaffMetrics | undefined => {
    if (mode !== 'day' || children.length !== 1) return undefined;
    const c = children[0]!;
    return c.rows.length === 1 && !c.is_manager && isShukkin(c.rows[0]!) ? c.rows[0] : undefined;
  };
  const kintaiCheck = mode === 'day' ? summaries[0]?.kintai_check : undefined;
  /** その日に OPレポートが取込済みか(勤怠の確認はその後) */
  const hasOpReport = mode === 'day' && !!summaries[0]?.sources?.op_report;
  /** 週次・月次でボタンを押したら日次に切り替えてから開く(常設。2026-09-10 指示) */
  const [kintaiPending, setKintaiPending] = useState(false);
  useEffect(() => {
    if (kintaiPending && mode === 'day' && !loading) {
      setKintaiPending(false);
      setKintaiOpen(true);
    }
  }, [kintaiPending, mode, loading]);
  const openKintai = () => {
    if (mode !== 'day') {
      period.setMode('day');
      setKintaiPending(true);
    } else {
      setKintaiOpen(true);
    }
  };

  /**
   * 取得数より右の列(時間別／通話・取得効率／BB稼働／売上・報酬・原価率)。色は現行シートの条件付き書式、吹き出しは出さない。
   * 合計行(isTotal)は現行シートと同じく AVG. の欄に通話料(通話分 × 通話料単価)を出す
   */
  const metricCells = (agg: Aggregate, business: BusinessType, unit: number | null, isTotal = false, shukkin?: StaffMetrics) => {
    const d = derived(agg);
    const callFee = Math.round((agg.talk_sec / 60) * callRate);
    /** 出勤(品川・タイミー)の日次行: 丸める前の値はカーソルを合わせた吹き出しで見せる(列幅を変えない)。手修正は「手」の印(D-56) */
    const hoursTitle = shukkin
      ? `ログイン ${fmtDuration(shukkin.login_sec)}(${(shukkin.login_sec / 3600).toFixed(2)}h)→拘束 ${shukkin.shift_hours ?? '—'}h ／ 休憩 ${fmtDuration(shukkin.break_sec)}(${(shukkin.break_sec / 3600).toFixed(2)}h)→休憩 ${shukkin.break_hours ?? '—'}h${shukkin.hours_source === 'manual' ? '(手修正)' : '(自動)'}`
      : undefined;
    return (
      <>
        {HOUR_BANDS.map((h, i) => {
          const n = agg.by_hour[i] ?? 0;
          return (
            <td key={h} className={`hour${i === 0 ? ' grp' : ''} ${isTotal ? '' : hourlyClass(n)}`}>
              {n}
            </td>
          );
        })}
        <td className="num grp">{agg.calls}</td>
        <td className={`num ${hitRateClass(d.hit_rate)}`}>{fmtPct(d.hit_rate)}</td>
        <td className={`num ${talkPerApptClass(d.talk_per_appt)}`}>{fmtDuration(d.talk_per_appt)}</td>
        <td className={`num ${workPerApptClass(d.work_per_appt)}`}>{fmtDuration(d.work_per_appt)}</td>
        <td className={`num ${apphClass(d.apph, business)}`}>{fmtNum(d.apph)}</td>
        <td className="num grp">{fmtDuration(agg.reception_sec)}</td>
        <td className="num">{fmtDuration(agg.talk_sec)}</td>
        <td className="num hint">{isTotal ? `${formatYen(callFee)}円` : fmtDuration(d.talk_avg)}</td>
        <td className="num">{fmtDuration(agg.after_sec)}</td>
        <td className="num hint">{isTotal ? '—' : fmtDuration(d.after_avg)}</td>
        <td className="num">{fmtDuration(d.work_sec)}</td>
        <td className="num" title={hoursTitle}>
          {fmtNum(agg.hours)}
          {shukkin?.hours_source === 'manual' && <span className="mark-manual">手</span>}
        </td>
        <td className="num">{fmtNum(d.calls_per_hour, 0)}</td>
        <td className="num grp">{isTotal ? '' : unit === null ? '—' : formatYen(unit)}</td>
        <td className="num">{formatYen(agg.revenue)}</td>
        <td className="num">{formatYen(agg.pay)}</td>
        <td className={`num ${costRateClass(d.cost_rate)}`}>{fmtPct(d.cost_rate)}</td>
      </>
    );
  };

  const groupTh = (label: string, span: number, help?: Help) => (
    <th colSpan={span} className="grp" style={{ textAlign: 'center', borderBottom: '1px solid var(--line)' }}>
      {help ? <HelpTip help={help}>{label}</HelpTip> : label}
    </th>
  );

  return (
    <div className="screen master-screen full">
      <PeriodToolbar
        period={period}
        right={
          isAdmin ? (
            <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button className="btn-line" style={{ padding: '4px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={openKintai} title={mode !== 'day' ? '日次に切り替えて、その日の出勤の勤怠を確認します' : undefined}>
                出勤の勤怠を確認{hasOpReport ? `(${shukkinRows.length}名)` : ''}
                {mode !== 'day' ? null : !hasOpReport ? (
                  <span className="badge b-pend">OP未取込</span>
                ) : kintaiCheck ? (
                  <span className="badge b-plan">確認済み {fmtKintaiCheck(kintaiCheck)}</span>
                ) : (
                  <span className="badge b-ng">未確認</span>
                )}
              </button>
              <button className="btn-line" style={{ padding: '4px 12px' }} onClick={() => setUploadOpen(true)}>
                CSVアップロード(OPレポート・対応履歴)
              </button>
            </span>
          ) : undefined
        }
      />
      {uploadOpen && (
        <CsvUploadModal
          defaultDate={range.anchor}
          companies={companies}
          onClose={() => setUploadOpen(false)}
          onImported={(ds) => {
            const d = ds[ds.length - 1];
            if (d && (d !== range.anchor || mode !== 'day')) period.setMode('day');
            if (d && d !== range.anchor) period.setAnchor(d);
            void reload();
          }}
        />
      )}
      {kintaiOpen && (
        <KintaiModal
          date={range.anchor}
          rows={shukkinRows}
          hasOpReport={hasOpReport}
          checked={kintaiCheck}
          companies={companies}
          onClose={() => setKintaiOpen(false)}
          onSaved={() => {
            void reload();
          }}
        />
      )}
      {loading ? (
        <div className="card">
          <div className="empty">読み込み中…</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <div className="empty">{range.label} の集計データがありません。{isAdmin && 'OPレポート・対応履歴のCSVをアップロードすると表示されます。'}</div>
        </div>
      ) : (
        <>
          <div className="card">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              日次【集計】({range.label})
              <span className="hint" style={{ fontWeight: 400 }}>
                行クリックで架電会社別を開閉{mode !== 'day' ? '(期間内を合算)' : ''}。見出しにカーソルを合わせると判定と色の意味
              </span>
              {mode === 'day' && (summaries[0]?.sources?.op_report || (summaries[0]?.sources?.activity ?? []).length > 0) && (
                <span className="hint" style={{ fontWeight: 400 }}>
                  取込: {summaries[0]?.sources?.op_report ? `OPレポート ${summaries[0].sources.op_report.file_name}` : 'OPレポート未取込(稼働・通話回数は0)'}
                  {(summaries[0]?.sources?.activity ?? []).length > 0
                    ? `／対応履歴 ${(summaries[0]?.sources?.activity ?? []).map((a) => `${a.business_codes.join('/') || '業務なし'} ${a.appointments}件`).join('・')}`
                    : '(取得件数は対応履歴の取込後)'}
                </span>
              )}
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button className="txn-link" onClick={() => setOpened(new Set(groups.map((x) => x.g.op_id)))}>
                  すべて開く
                </button>
                <button className="txn-link" onClick={() => setOpened(new Set())}>
                  すべて閉じる
                </button>
              </span>
            </h3>

            <div className="toolbar" style={{ marginBottom: 10 }}>
              <div className="tool-filter" style={{ width: '100%', rowGap: 6 }}>
                <MultiSelect label="出勤形態" options={styleOptions} value={styleF} onChange={setStyleF} />
                <MultiSelect label="架電内容" options={(Object.keys(BUSINESS_LABELS) as BusinessType[]).map((k) => ({ key: k, label: BUSINESS_LABELS[k] }))} value={bizF} onChange={setBizF} />
                <MultiSelect label="架電会社" options={companyOptions.map((c) => ({ key: c.id, label: c.name }))} value={companyF} onChange={setCompanyF} />
                <SearchBox value={keyword} onChange={setKeyword} placeholder="名前・BB IDで検索" />
              </div>
            </div>

            <div className="table-scroll">
              <table className="fit-table">
                <thead>
                  <tr>
                    <SortTh label="RANK" sortKey="rank" sort={sort} onSort={toggleSort} help={HELP.rank} rowSpan={2} className="c sticky-1" />
                    <SortTh label="アポインター" sortKey="name" sort={sort} onSort={toggleSort} defaultDir="asc" rowSpan={2} className="sticky-2" />
                    <SortTh label="出勤形態" sortKey="style" sort={sort} onSort={toggleSort} defaultDir="asc" rowSpan={2} className="c" />
                    <SortTh label="報酬" sortKey="wage" sort={sort} onSort={toggleSort} rowSpan={2} className="c" help={HELP.wage} />
                    <SortTh label="架電会社" sortKey="company" sort={sort} onSort={toggleSort} defaultDir="asc" rowSpan={2} className="c" />
                    <SortTh label="架電内容" sortKey="business" sort={sort} onSort={toggleSort} defaultDir="asc" rowSpan={2} className="c" />
                    <SortTh label="取得数" sortKey="appointments" sort={sort} onSort={toggleSort} rowSpan={2} className="c" />
                    {groupTh('時間別取得件数(時台)', HOUR_BANDS.length, HELP.hourly)}
                    {groupTh('通話・取得効率', 5)}
                    {groupTh('BB稼働時間の内訳', 8)}
                    {groupTh('売上・報酬・原価率', 4)}
                  </tr>
                  <tr>
                    {HOUR_BANDS.map((h, i) => (
                      <th key={h} className={`hour${i === 0 ? ' grp' : ''}`}>
                        {h}
                      </th>
                    ))}
                    <SortTh label="通話回数" sortKey="calls" sort={sort} onSort={toggleSort} className="num grp" />
                    <SortTh label="取得率" sortKey="hit_rate" sort={sort} onSort={toggleSort} className="num" help={HELP.hitRate} />
                    <SortTh label="通話/取得" sortKey="talk_per_appt" sort={sort} onSort={toggleSort} className="num" defaultDir="asc" help={HELP.talkPerAppt} />
                    <SortTh label="稼働/取得" sortKey="work_per_appt" sort={sort} onSort={toggleSort} className="num" defaultDir="asc" help={HELP.workPerAppt} />
                    <SortTh label="見込/h" sortKey="apph" sort={sort} onSort={toggleSort} className="num" help={HELP.apph} />
                    <SortTh label="受付" sortKey="reception" sort={sort} onSort={toggleSort} className="num grp" />
                    <SortTh label="通話" sortKey="talk" sort={sort} onSort={toggleSort} className="num" />
                    <th className="num">AVG.</th>
                    <SortTh label="後処理" sortKey="after" sort={sort} onSort={toggleSort} className="num" />
                    <th className="num">AVG.</th>
                    <SortTh label="稼働合計" sortKey="work" sort={sort} onSort={toggleSort} className="num" help={HELP.workSec} />
                    <SortTh label="実稼働(h)" sortKey="hours" sort={sort} onSort={toggleSort} className="num" />
                    <SortTh label="通話/h" sortKey="calls_per_hour" sort={sort} onSort={toggleSort} className="num" />
                    <SortTh label="会社単価" sortKey="unit" sort={sort} onSort={toggleSort} className="num grp" />
                    <SortTh label="売上見込" sortKey="revenue" sort={sort} onSort={toggleSort} className="num" help={HELP.revenue} />
                    <SortTh label="報酬額見込" sortKey="pay" sort={sort} onSort={toggleSort} className="num" help={HELP.pay} />
                    <SortTh label="原価率" sortKey="cost_rate" sort={sort} onSort={toggleSort} className="num" defaultDir="asc" help={HELP.costRate} />
                  </tr>
                </thead>
                <tbody>
                  {groups.length === 0 && (
                    <tr>
                      <td colSpan={colCount} className="hint">
                        絞り込みに該当するスタッフがいません
                      </td>
                    </tr>
                  )}
                  {groups.map(({ g, children }) => {
                    const open = opened.has(g.op_id);
                    const wage = groupWage(g);
                    const unit = groupUnit(g);
                    return [
                      <tr key={g.op_id} className="tree-l0 clickable" onClick={() => toggleOpen(g.op_id)} aria-expanded={open}>
                        <td className="c sticky-1">
                          <span className={`badge ${rankClass(g.rank)}`}>{g.rank}</span>
                        </td>
                        <td className="sticky-2">
                          <span className={`chev${open ? ' open' : ''}`}>
                            <IconChevronRight size={12} />
                          </span>
                          {g.name}
                        </td>
                        <td className="c">{g.style}</td>
                        <td className="c">{wage === null ? '—' : formatYen(wage)}</td>
                        <td className="c">{children.length === 1 ? companyName(children[0]!.company_id) : `${children.length}社`}</td>
                        <td className="c">{g.businesses}</td>
                        <td className="c">{g.agg.appointments}</td>
                        {metricCells(g.agg, g.business, unit, false, shukkinOf(children))}
                      </tr>,
                      ...(open
                        ? children.map((c) => (
                            <tr key={`${g.op_id}_${c.company_id ?? ''}`} className="tree-l1">
                              <td className="sticky-1"></td>
                              <td className="sticky-2 hint" style={{ paddingLeft: 22 }}>
                                └
                              </td>
                              <td className="c hint">{c.style}</td>
                              <td className="c">{c.is_manager || c.wage === null ? '—' : formatYen(c.wage)}</td>
                              <td className="c">
                                {companyName(c.company_id)}
                                {mode !== 'day' && <span className="hint"> {c.days}日</span>}
                              </td>
                              <td className="c">
                                <span className={`badge ${c.business === 'kyutoki' ? 'b-plan' : 'b-pend'}`}>{BUSINESS_LABELS[c.business]}</span>
                              </td>
                              <td className="c">{c.agg.appointments}</td>
                              {metricCells(
                                c.agg,
                                c.business,
                                c.unit_price,
                                false,
                                shukkinOf([c]),
                              )}
                            </tr>
                          ))
                        : []),
                    ];
                  })}
                </tbody>
                <tfoot>
                  {subtotals.rows
                    .filter((s) => s.agg.count > 0)
                    .map((s) => (
                      <tr key={s.key} className="subtotal">
                        <td className="c sticky-1"></td>
                        <td className="c sticky-2">{s.label}</td>
                        <td className="c hint">合計</td>
                        <td colSpan={3}></td>
                        <td className="c">{s.agg.appointments}</td>
                        {metricCells(s.agg, s.key === 'white' ? 'fuyohin' : 'kyutoki', null, true)}
                      </tr>
                    ))}
                  <tr className="total" style={{ fontWeight: 700 }}>
                    <td className="c sticky-1">
                      <span className={`badge ${rankClass(rankOf(apphOf(subtotals.kyutoki), 'kyutoki'))}`}>{rankOf(apphOf(subtotals.kyutoki), 'kyutoki')}</span>
                    </td>
                    <td className="c sticky-2">合計{subtotals.hasWhite ? '(給湯器)' : ''}</td>
                    <td className="c hint">{filteredRows.length !== rows.length ? '絞り込み後' : ''}</td>
                    <td colSpan={3}></td>
                    <td className="c">{subtotals.kyutoki.appointments}</td>
                    {metricCells(subtotals.kyutoki, 'kyutoki', null, true)}
                  </tr>
                  {subtotals.hasWhite && (
                    <tr className="total" style={{ fontWeight: 700 }}>
                      <td className="c sticky-1"></td>
                      <td className="c sticky-2">合計(ホワイト含む)</td>
                      <td colSpan={4}></td>
                      <td className="c">{total.appointments}</td>
                      {metricCells(total, 'kyutoki', null, true)}
                    </tr>
                  )}
                  <tr className="hint">
                    <td colSpan={2} className="sticky-1">
                      時間帯構成比
                    </td>
                    <td colSpan={5}></td>
                    {HOUR_BANDS.map((h, i) => (
                      <td key={h} className={`hour${i === 0 ? ' grp' : ''}`}>
                        {total.appointments > 0 ? fmtPct(((total.by_hour[i] ?? 0) / total.appointments) * 100, 1) : '—'}
                      </td>
                    ))}
                    <td colSpan={colCount - BASIC - HOUR_BANDS.length}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

        </>
      )}
    </div>
  );
}
